import {cast as castArray, intersperse} from './array'
import CMPError from '../cmp-error'
import {camelCase} from './string'
import {curry, CurriedFunction2} from './function'
import {I18n} from '../i18n'
import PolyGlot from 'node-polyglot'

export interface ObjectAttribute {[key: string]: boolean}
export type ArrayAttribute = string[]
export type Attribute = boolean | number | string | ArrayAttribute | ObjectAttribute
export interface Attributes {[key: string]: Attribute}
export type Child = string | ElementDecorator
export type Children = Child[]
export type ViewTranslator = (key: string, replacements?: {[key: string]: Child}) => Children

export class ElementDecorator {
  constructor (public raw: HTMLElement) {}

  public on (event: string, fns: EventListener | EventListener[]): ElementDecorator {
    for (const fn of castArray(fns)) {
      this.raw.addEventListener(event, fn as EventListener)
    }
    return this
  }

  public click (fns: EventListener | EventListener[]): ElementDecorator {
    return this.on('click', fns)
  }

  public remove (): ElementDecorator {
    const parent = this.raw.parentNode

    if (parent) {
      parent.removeChild(this.raw)
    }

    return this
  }

  public replace (elementDecorator: ElementDecorator): ElementDecorator {
    const parent = this.raw.parentNode

    if (!parent) {
      throw new CMPError('Cannot replace element')
    }

    parent.replaceChild(elementDecorator.raw, this.raw)
    return elementDecorator
  }

  public submit (fns: EventListener | EventListener[]): ElementDecorator {
    return this.on('submit', fns)
  }

  public appendChild (child: ElementDecorator): ElementDecorator {
    this.raw.appendChild(child.raw)
    return this
  }
}

const setAttributes = (element: HTMLElement, attributes: Attributes): void => {
  for (const [key, attribute] of Object.entries(attributes)) {
    let value

    if (Array.isArray(attribute)) {
      value = attribute.join(' ')
    } else if (typeof attribute === 'object') {
      value = Object.keys(attribute).filter(key => attribute[key]).join(' ')
    } else if (typeof attribute === 'boolean') {
      value = ''
    } else {
      value = '' + attribute
    }

    element.setAttribute(key, value)
  }
}

const appendChildren = (
  element: Element,
  children: Children,
  doc: Document
): void => {
  for (const child of children) {
    element.appendChild(
      typeof child === 'string' ? doc.createTextNode(child) : child.raw
    )
  }
}

const TRANSLATE_REG_EXP = /(%\{([^\}]+)\})/g
export const translate = curry((i18n: I18n, key: string, replacements: {[key: string]: Child} = {}): Children => {
  const children = i18n.t(key).split(TRANSLATE_REG_EXP)
  const translated: Children = children.slice(0, 1)

  for (let i = 1; i < children.length; i++) {
    const child = children[i]
    translated.push(TRANSLATE_REG_EXP.test(child) ? replacements[children[++i]] : child)
  }

  return translated
})

export const $ = (
  name: string,
  attributes: Attributes = {},
  children: Child | Children = [],
  doc: Document = document
): ElementDecorator => {
  const element: HTMLElement = doc.createElement(name)
  setAttributes(element, attributes)
  appendChildren(element, Array.isArray(children) ? children : [children], doc)
  return new ElementDecorator(element as HTMLElement)
}

export const text = (str: string, doc: Document = document): Children =>
  intersperse(
    () => new ElementDecorator(doc.createElement('br')),
    str.split('\n')
  )

export const ps = (str: string, attrs: Attributes = {}, doc: Document = document): Children =>
  str.split('\n').map(para => $('p', attrs, para))
